import "./App.css";
import React, { useEffect, useState, useMemo, useCallback } from "react";
import SplashScreen from "./module/SplashScreen";
import ErrorPage from "./module/ErrorPage";
import QRCodePage from "./module/QRCodePage";
import { useTranslation } from "react-i18next";
import { Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import PaymentPage from "./module/Payment";
import PaymentSuccessPage from "./module/PaymentSuccess";
import PaymentReturnPage from "./module/PaymentReturn";
import CurrentOrderPage from "./module/CurrentOrder";
import OrderSubmitPage from "./module/OrderSubmit";
import CustomizePage from "./module/Customize";
import AssistantPage from "./module/Assistant";
import MainContent from "./Components/MainContent";
import NameModal from "./Components/NameModal";
import MainLayout from "./Components/MainLayout";
import useMenuData from "./hooks/useMenuData";
import useOrderManagement from "./hooks/useOrderManagement";
import { useAppContext } from "./context/AppContext";
import { getPrice, getTipValue, getOrderSummary } from "./util/helper";
import { ALL_TAB, FAVORITES_TAB } from "./util/menuTabs";
import useFavorites from "./hooks/useFavorites";
import { readScopedGuestDisplayName } from "./util/cookies";
import {
  opmStorage,
  Keys,
  DEFAULT_TTL_SECONDS,
} from "./util/opmStorage";

function App() {
  const [searchTerm, setSearchTerm] = useState("");
  const [name, setName] = useState(() => readScopedGuestDisplayName());
  const [cookingLevel, setCookingLevel] = useState([]);
  const [splitOptions, setSplitOptions] = useState("Split by person");
  const [selectedTip, setSelectedTip] = useState("15");
  const [enterTip, setEnterTip] = useState("");
  const [selectedPerson, setSelectedPerson] = useState("");
  const [removeIngredients, setRemoveIngredients] = useState([]);
  const [notes, setNotes] = useState("");
  const [eachPerson, setEachPerson] = useState(1);
  const [wishAmount, setWishAmount] = useState("");
  const [showNameModal, setShowNameModal] = useState(false);
  const [headerHeight, setHeaderHeight] = useState(0);
  const [bottomHeight, setBottomHeight] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [swiperShowDelete, setSwiperShowDelete] = useState([]);
  const { t, i18n } = useTranslation();
  const {
    language,
    isSplashScreen,
    guestRestaurantId,
    guestBrowseReady,
    storageElementId,
    setCategoryList,
    setSelectedTab,
    selectedTab,
    selectedItem,
    setSelectedItem,
    isAuthenticated,
    authRestoring,
    checkoutOrder,
    setCheckoutOrder,
  } = useAppContext();

  const canAccessApp =
    isAuthenticated ||
    (Boolean(guestRestaurantId) && guestBrowseReady);
  const menuData = useMenuData();
  const { favoriteIds } = useFavorites();
  const {
    orderList,
    setOrderList,
    setCurrentOrderList,
    isOrderSubmitted,
    handleDeleteItem,
    handleChangeQuantity,
    handleSubmit,
  } = useOrderManagement();

  const setPaymentOrderList = useCallback(
    (updater) => {
      if (checkoutOrder != null) {
        setCheckoutOrder((prev) =>
          typeof updater === "function" ? updater(prev) : updater
        );
      } else {
        setOrderList((prev) =>
          typeof updater === "function" ? updater(prev) : updater
        );
      }
    },
    [checkoutOrder, setCheckoutOrder, setOrderList]
  );

  const paymentOrderList = checkoutOrder ?? orderList;

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    i18n.changeLanguage(language);
  }, [language, i18n]);

  useEffect(() => {
    if (!menuData?.categories?.length) return;
    const seen = new Set();
    const uniqueCategories = menuData.categories.filter((cat) => {
      const name = cat?.translations?.name;
      if (!name) return false;
      return seen.has(name) ? false : seen.add(name);
    });
    setCategoryList(uniqueCategories);
    if (
      selectedTab !== ALL_TAB &&
      selectedTab !== FAVORITES_TAB &&
      !uniqueCategories.some((cat) => cat?.translations?.name === selectedTab)
    ) {
      setSelectedTab(ALL_TAB);
    }
    setSplitOptions(t("Split by person"));
  }, [language, menuData, selectedTab, setCategoryList, setSelectedTab, t]);

  useEffect(() => {
    if (location.pathname === "/") {
      setSelectedItem(null);
      setNotes("");
      setQuantity(1);
    }
  }, [location, setSelectedItem]);

  useEffect(() => {
    if (!guestRestaurantId || !storageElementId) return;
    setName(readScopedGuestDisplayName());
  }, [guestRestaurantId, storageElementId]);

  useEffect(() => {
    if (selectedItem) {
      const singleOption = selectedItem?.options?.find(
        (opt) => opt.selection_type === "single_choice"
      );
      setCookingLevel((prev) =>
        prev?.length > 0
          ? [...prev]
          : singleOption?.values?.[0]?.name
          ? [singleOption.values[0].name]
          : []
      );
      // Extras start unchosen: the server charges for every extra sent with the order.
      setRemoveIngredients((prev) => (prev?.length > 0 ? [...prev] : []));
      if (selectedItem?.edit) {
        setNotes(selectedItem.notes || "");
        setQuantity(selectedItem.quantity || 1);
      }
    }
  }, [selectedItem]);

  const filteredItems = useMemo(() => {
    const term = searchTerm?.toLowerCase()?.trim();
    if (term) {
      return (
        menuData?.categories?.flatMap((cat) =>
          (cat?.items || []).filter((cItem) =>
            cItem?.translations?.name?.toLowerCase()?.includes(term)
          )
        ) || []
      );
    }
    const allItems = menuData?.categories?.flatMap((cat) => cat?.items || []) || [];
    if (selectedTab === ALL_TAB) return allItems;
    if (selectedTab === FAVORITES_TAB) {
      return allItems.filter((item) => favoriteIds.includes(item.menu_item_id));
    }
    return (
      menuData?.categories
        ?.find((item) => item?.translations?.name === selectedTab)
        ?.items || []
    );
  }, [menuData, selectedTab, searchTerm, favoriteIds]);

  // Public routes bypass the splash screen entirely
  const publicPaths = ["/generate-qr", "/error"];
  if (publicPaths.includes(location.pathname)) {
    return (
      <Routes>
        <Route path="/error" element={<ErrorPage />} />
        <Route path="/generate-qr" element={<QRCodePage />} />
      </Routes>
    );
  }

  // Show splash screen while loading the public menu
  if (isSplashScreen) return <SplashScreen />;

  return (
    <>
      <Routes>
        {/* Public routes - accessible without authentication */}
        <Route path="/error" element={<ErrorPage />} />
        <Route path="/generate-qr" element={<QRCodePage />} />

        {/* Protected routes - require valid token */}
        <Route
          path="/"
          element={
            canAccessApp ? (
              <MainLayout>
                <MainContent
                  searchTerm={searchTerm}
                  setSearchTerm={setSearchTerm}
                  setHeaderHeight={setHeaderHeight}
                  headerHeight={headerHeight}
                  Container={require("./Components/Container").default}
                  filteredItems={filteredItems}
                  t={t}
                  orderList={orderList}
                  navigate={navigate}
                  setCookingLevel={setCookingLevel}
                  setRemoveIngredients={setRemoveIngredients}
                  setBottomHeight={setBottomHeight}
                  bottomHeight={bottomHeight}
                  setSelectedItem={setSelectedItem}
                />
              </MainLayout>
            ) : (
              <Navigate to="/error?reason=missing_restaurant" replace />
            )
          }
        />
        <Route
          path="/current-order"
          element={
            canAccessApp ? (
              <CurrentOrderPage
                closeModal={() => navigate("/")}
                setSwiperShowDelete={setSwiperShowDelete}
                isOrderSubmitted={isOrderSubmitted}
                t={t}
                orderList={orderList}
                getPrice={getPrice}
                handleDeleteItem={handleDeleteItem}
                handleChangeQuantity={handleChangeQuantity}
                swiperShowDelete={swiperShowDelete}
                navigate={navigate}
                setSelectedPerson={setSelectedPerson}
                setOrderList={setOrderList}
                setCurrentOrderList={setCurrentOrderList}
                name={name}
                setName={setName}
                setShowNameModal={setShowNameModal}
                setCookingLevel={setCookingLevel}
                setRemoveIngredients={setRemoveIngredients}
              />
            ) : (
              <Navigate to="/error?reason=missing_restaurant" replace />
            )
          }
        />
        <Route
          path="/customize/:itemName"
          element={
            canAccessApp ? (
              <MainLayout>
                <CustomizePage
                  closeModal={() => {
                    navigate("/");
                    setSelectedItem(null);
                  }}
                  onAdded={(addedDish) => navigate("/", { state: { addedDish } })}
                  handleSubmit={(onClose, items) => {
                    handleSubmit(onClose, items, {
                      cookingLevel,
                      removeIngredients,
                      notes,
                      setNotes,
                      setCookingLevel,
                      setRemoveIngredients,
                      setSelectedItem,
                    });
                  }}
                  customizeSelectedItem={selectedItem}
                  cookingLevel={cookingLevel}
                  setCookingLevel={setCookingLevel}
                  removeIngredients={removeIngredients}
                  setRemoveIngredients={setRemoveIngredients}
                  notes={notes}
                  setNotes={setNotes}
                  t={t}
                  setQuantity={setQuantity}
                  quantity={quantity}
                />
              </MainLayout>
            ) : (
              <Navigate to="/error?reason=missing_restaurant" replace />
            )
          }
        />
        <Route
          path="/assistant"
          element={
            canAccessApp ? (
              <MainLayout>
                <AssistantPage
                  orderList={orderList}
                  setCookingLevel={setCookingLevel}
                  setRemoveIngredients={setRemoveIngredients}
                  setSelectedItem={setSelectedItem}
                />
              </MainLayout>
            ) : (
              <Navigate to="/error?reason=missing_restaurant" replace />
            )
          }
        />
        <Route
          path="/order-submit"
          element={
            isAuthenticated ? (
              <OrderSubmitPage
                closeModal={() => navigate("/current-order")}
                t={t}
                setCurrentOrderList={setCurrentOrderList}
                navigate={navigate}
              />
            ) : authRestoring ? null : (
              <Navigate to="/error?reason=missing_token" replace />
            )
          }
        />
        <Route
          path="/payment"
          element={
            isAuthenticated ? (
              <PaymentPage
                closeModal={() => navigate("/current-order")}
                t={t}
                getPrice={getPrice}
                orderList={paymentOrderList}
                wishAmount={wishAmount}
                setWishAmount={setWishAmount}
                splitOptions={splitOptions}
                setSplitOptions={setSplitOptions}
                selectedTip={selectedTip}
                setSelectedTip={setSelectedTip}
                enterTip={enterTip}
                setEnterTip={setEnterTip}
                setOrderList={setPaymentOrderList}
                getOrderSummary={getOrderSummary}
                selectedPerson={selectedPerson}
                setSelectedPerson={setSelectedPerson}
                eachPerson={eachPerson}
                setEachPerson={setEachPerson}
                setIsOrderSubmitted={() => {}}
                navigate={navigate}
              />
            ) : authRestoring ? null : (
              <Navigate to="/error?reason=missing_token" replace />
            )
          }
        />
        {/* Back from the payment page: the app reloads here, before the table's token is
            restored, so it needs only the restaurant (the page reads the token itself). */}
        <Route
          path="/payment-return"
          element={
            canAccessApp ? (
              <PaymentReturnPage t={t} navigate={navigate} />
            ) : (
              <Navigate to="/error?reason=missing_restaurant" replace />
            )
          }
        />
        <Route
          path="/payment-success"
          element={
            isAuthenticated ? (
              <PaymentSuccessPage
                closeModal={() => navigate("/")}
                t={t}
                setOrderList={setOrderList}
                setCurrentOrderList={setCurrentOrderList}
                setCheckoutOrder={setCheckoutOrder}
                navigate={navigate}
                orderList={paymentOrderList}
                getPrice={getPrice}
                getTipValue={getTipValue}
              />
            ) : authRestoring ? null : (
              <Navigate to="/error?reason=missing_token" replace />
            )
          }
        />

        {/* Catch-all: redirect unknown routes to error */}
        <Route path="*" element={<Navigate to="/error?reason=missing_restaurant" replace />} />
      </Routes>
      <NameModal
        show={showNameModal}
        onClose={() => setShowNameModal(false)}
        onConfirm={(confirmedName) => {
          const trimmed = confirmedName?.trim();
          if (trimmed) {
            const r = guestRestaurantId;
            const e = storageElementId;
            if (r && e) {
              opmStorage.setWithTtl(
                Keys.guestName(r, e),
                trimmed,
                DEFAULT_TTL_SECONDS
              );
            }
            setName(trimmed);
          }
        }}
        name={name}
        setName={setName}
        t={t}
        language={language}
      />
    </>
  );
}

export default App;
